<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { computed, inject, onBeforeMount, onBeforeUnmount, ref } from "vue"
import { useI18n } from "vue-i18n"
import moment from "moment-timezone"
import { useToast } from "primevue/usetoast"
import type { BreadcrumbItem } from "@core/components/layout/header/HeaderPage.vue"
import { formatDate, formatNumber } from "@core/helpers"
import { useTaskStore } from "@core/stores/task.store"
import type { Services } from "@services"
import type { OptimDashboardKpiItem } from "@modules/courtage/components/optimturpe/OptimDashboardKpiSection.vue"
import { OPTIM_TURPE_DASHBOARD_ANALYSIS_SUCCESS } from "@modules/courtage/constants/optim-turpe"
import {
    buildOptimTurpeDashboardTableRows,
    getOptimTurpeDashboardStat,
} from "@modules/courtage/composables/useOptimTurpeDashboardRows"
import { useOptimTurpeDashboardStore } from "@modules/courtage/stores/optim-turpe-dashboard.store"

defineOptions({
    name: "PageDashboardOptimisationContractuelle",
})

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            /** Aligné sur `lang/optimisation-contractuelle/pages.lang.js` (legacy). */
            breadcrumbAccueil: "Accueil",
            pageTitle: "Économies sur tout le parc",
            lastUpdate: "Dernière mise à jour le",
            downloadReport: "Exporter les résultats",
            kpiHtva: "HTVA",
            totalSection: "Économies annuelles identifiées",
            meanSection: "Économies annuelles moyennes",
            otherSection: "Autres informations utiles",
            detailsSection: "Détails des économies par compteur",
            emptyTable: "Aucun résultat à afficher.",
            colSegment: "Segment",
            colIdsource: "Compteur",
            colAddress: "Adresse",
            colEconomy: "Économies",
            filterC5: "C5",
            filterC4: "C4",
            filterC2: "C2",
            modalTitle: "Exporter les résultats",
            modalMessage: "Choisissez les types de compteurs à exporter :",
            modalChoice1: "Résultats des compteurs C5",
            modalChoice2: "Résultats des compteurs C4",
            modalChoice3: "Résultats des compteurs C2",
            modalConfirm: "Exporter",
            modalCancel: "Annuler",
            exportError:
                "Une erreur s'est produite lors de l'export. Veuillez réessayer ultérieurement.",
            toastExcelTitle: "Génération du rapport Excel",
            toastExcelSuccess: "Rapport prêt.",
            toastExcelFailure: "Échec de la génération du rapport.",
            kpiTotal: "au total",
            kpiTotalC5: "sur les compteurs C5",
            kpiTotalC4: "sur les compteurs C4",
            kpiTotalC2: "sur les compteurs C2",
            kpiMeanC5: "par compteur C5",
            kpiMeanC4: "par compteur C4",
            kpiMeanC2: "par compteur C2",
            kpiNbPdlOne: "compteur dans le parc",
            kpiNbPdlMany: "compteurs dans le parc",
            kpiNbOptiOne: "compteur analysé",
            kpiNbOptiMany: "compteurs analysés",
            kpiNbOptiHintOne:
                "Compteur pour lequel des économies ont été identifiées",
            kpiNbOptiHintMany:
                "Compteurs pour lesquels des économies ont été identifiées",
            kpiNbErrZero: "aucun compteur en attente d'étude",
            kpiNbErrOne: "compteur en attente d'étude",
            kpiNbErrMany: "compteurs en attente d'étude",
            kpiNbErrHintOne:
                "Compteur qui n'a pas encore assez d'historique pour faire l'analyse",
            kpiNbErrHintMany:
                "Compteurs qui n'ont pas encore assez d'historique pour faire l'analyse",
        },
    },
})

// ================================
// COMPOSABLES & SERVICES
// ================================
const dashboardStore = useOptimTurpeDashboardStore()
const taskStore = useTaskStore()
const services = inject<Services>("services")
const toast = useToast()

// ================================
// STATE
// ================================
const isExportDialogVisible = ref(false)
const defaultExportSegments = ref<[boolean, boolean, boolean]>([
    true,
    true,
    true,
])

// ================================
// DONNÉES CALCULÉES
// ================================
const getSynthesis = computed(() => dashboardStore.synthesis)

/**
 * Fil d'Ariane (Accueil → titre de la page), comme les autres dashboards.
 */
const getBreadcrumb = computed<BreadcrumbItem[]>(() => [
    {
        icon: "pi pi-home",
        label: t("breadcrumbAccueil"),
        route: "/",
    },
    { icon: "pi pi-chart-line", label: t("pageTitle") },
])

const getLastUpdate = computed(() => {
    const s = getSynthesis.value
    if (!s) return ""
    /** Même ordre de lecture que le dashboard legacy (C4, C5, C2). */
    const dates = (["C4", "C5", "C2"] as const)
        .map((k) => {
            const w = s[`workflowanalyses_${k}`]
            return w?.createdAt ?? w?.created_at
        })
        .filter(Boolean) as string[]
    const moments = dates.map((d) => moment(d)).filter((m) => m.isValid())
    if (!moments.length) return formatDate(null)
    return formatDate(moment.max(moments).toISOString())
})

const getTableRows = computed(() =>
    buildOptimTurpeDashboardTableRows(getSynthesis.value)
)

const getHasTableData = computed(
    () => (getSynthesis.value?.analyses?.length ?? 0) > 0
)

const getShowExportButton = computed(
    () =>
        getHasTableData.value &&
        !dashboardStore.fetchErrorMessage &&
        !dashboardStore.isFetchingDashboard
)

function formatCurrency(value: number | undefined): string {
    if (value === undefined || Number.isNaN(value)) return "—"
    return formatNumber(value, {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
    })
}

/** Affichage des effectifs comme le legacy (`?? '-'`). */
function formatCount(value: number | undefined): string | number {
    if (value === undefined || Number.isNaN(value)) return "-"
    return value
}

function getLabelParcMeters(count: number | undefined): string {
    return (count ?? 0) === 1 ? t("kpiNbPdlOne") : t("kpiNbPdlMany")
}

function getLabelAnalysed(count: number | undefined): string {
    return (count ?? 0) === 1 ? t("kpiNbOptiOne") : t("kpiNbOptiMany")
}

function getLabelPendingStudy(count: number | undefined): string {
    const n = count ?? 0
    if (n === 0) return t("kpiNbErrZero")
    if (n === 1) return t("kpiNbErrOne")
    return t("kpiNbErrMany")
}

function getHintAnalysed(count: number | undefined): string {
    return (count ?? 0) === 1 ? t("kpiNbOptiHintOne") : t("kpiNbOptiHintMany")
}

function getHintPending(count: number | undefined): string {
    return (count ?? 0) === 1 ? t("kpiNbErrHintOne") : t("kpiNbErrHintMany")
}

const getTotalKpis = computed((): OptimDashboardKpiItem[] => {
    const s = getSynthesis.value
    return [
        {
            type: "eco_total",
            label: t("kpiTotal"),
            value: formatCurrency(
                getOptimTurpeDashboardStat(s, "economies_total")
            ),
        },
        {
            type: "eco_c5",
            label: t("kpiTotalC5"),
            value: formatCurrency(
                getOptimTurpeDashboardStat(s, "economies_C5")
            ),
        },
        {
            type: "eco_c4",
            label: t("kpiTotalC4"),
            value: formatCurrency(
                getOptimTurpeDashboardStat(s, "economies_C4")
            ),
        },
        {
            type: "eco_c2",
            label: t("kpiTotalC2"),
            value: formatCurrency(
                getOptimTurpeDashboardStat(s, "economies_C2")
            ),
        },
    ]
})

const getMeanKpis = computed((): OptimDashboardKpiItem[] => {
    const s = getSynthesis.value
    return [
        {
            type: "mean_c5",
            label: t("kpiMeanC5"),
            value: formatCurrency(
                getOptimTurpeDashboardStat(s, "economies_mean_C5")
            ),
        },
        {
            type: "mean_c4",
            label: t("kpiMeanC4"),
            value: formatCurrency(
                getOptimTurpeDashboardStat(s, "economies_mean_C4")
            ),
        },
        {
            type: "mean_c2",
            label: t("kpiMeanC2"),
            value: formatCurrency(
                getOptimTurpeDashboardStat(s, "economies_mean_C2")
            ),
        },
    ]
})

const getOtherKpis = computed((): OptimDashboardKpiItem[] => {
    const s = getSynthesis.value
    const nb = getOptimTurpeDashboardStat(s, "nb_pdl")
    const nbOpti = getOptimTurpeDashboardStat(s, "nb_pdl_opti")
    const nbErr = getOptimTurpeDashboardStat(s, "nb_pdl_missing_data")
    return [
        {
            type: "nb_pdl",
            label: getLabelParcMeters(nb),
            value: formatCount(nb),
        },
        {
            type: "nb_opti",
            label: getLabelAnalysed(nbOpti),
            value: formatCount(nbOpti),
            hint: getHintAnalysed(nbOpti),
        },
        {
            type: "nb_err",
            label: getLabelPendingStudy(nbErr),
            value: formatCount(nbErr),
            hint: getHintPending(nbErr),
        },
    ]
})

// ================================
// INTERACTIONS
// ================================
function onClickOpenExportDialog(): void {
    isExportDialogVisible.value = true
}

/**
 * Exporte les segments sélectionnés : URL valide ou relance tâche Excel.
 */
async function onExportConfirmed(payload: {
    c5: boolean
    c4: boolean
    c2: boolean
}): Promise<void> {
    const segments: ("C5" | "C4" | "C2")[] = []
    if (payload.c5) segments.push("C5")
    if (payload.c4) segments.push("C4")
    if (payload.c2) segments.push("C2")

    const synthesis = getSynthesis.value
    if (!synthesis || !services?.$generateDashboardAnalyseReport) {
        toast.add({
            severity: "error",
            summary: t("exportError"),
            life: 6000,
        })
        return
    }

    try {
        for (const segment of segments) {
            const key = `workflowanalyses_${segment}` as const
            const block = synthesis[key]
            if (block?.status !== OPTIM_TURPE_DASHBOARD_ANALYSIS_SUCCESS) {
                continue
            }
            const urlExcel = block.url_excel
            const expired = block.date_expiration_rapport
            if (urlExcel && expired && new Date(expired) > new Date()) {
                window.location.assign(urlExcel)
                continue
            }
            if (!block._id) continue
            const { task_id, event_name } =
                await services.$generateDashboardAnalyseReport({
                    analyseId: block._id,
                })
            taskStore.followEvent(task_id, event_name, {
                taskname: "dashboard.optimturpe.export_excel",
                notification: true,
                autoDelete: true,
                taskToastTitle: t("toastExcelTitle"),
                taskToastSuccessMessage: t("toastExcelSuccess"),
                taskToastFailureMessage: t("toastExcelFailure"),
                onSuccess: (data: {
                    data?: { result?: { url_excel?: string } }
                }) => {
                    const url = data?.data?.result?.url_excel
                    if (url) {
                        window.open(url, "_blank")
                    }
                },
            })
        }
    } catch {
        toast.add({
            severity: "error",
            summary: t("exportError"),
            life: 6000,
        })
    }
}

// ================================
// LIFECYCLE
// ================================
onBeforeMount(async () => {
    await dashboardStore.fetchDashboard()
})

onBeforeUnmount(() => {
    dashboardStore.resetDashboard()
})
</script>

<template>
    <!-- ================================ -->
    <!-- PAGE DASHBOARD OPTIM TURPE -->
    <!-- ================================ -->
    <div
        class="page page-dashboard-optim-turpe flex min-h-0 flex-1 flex-col gap-8 p-6"
    >
        <!-- ================================ -->
        <!-- EN-TÊTE (fil d’Ariane + titre, comme PageDashboardIot) -->
        <!-- ================================ -->
        <HeaderPage
            :title="t('pageTitle')"
            :breadcrumb="getBreadcrumb"
            class-header="flex flex-wrap items-start justify-between gap-4"
        >
            <template #subtitle>
                <p
                    v-if="getSynthesis && getLastUpdate"
                    class="text-sm text-surface-600"
                >
                    {{ t("lastUpdate") }}
                    <span class="font-medium text-surface-800">{{
                        getLastUpdate
                    }}</span>
                </p>
            </template>
            <BaseButton
                v-if="getShowExportButton"
                :label="t('downloadReport')"
                :title="t('downloadReport')"
                severity="secondary"
                text
                class="shrink-0"
                @click="onClickOpenExportDialog"
            />
        </HeaderPage>

        <BaseMessage
            v-if="dashboardStore.fetchErrorMessage"
            severity="error"
            :closable="false"
            class="w-full max-w-6xl mx-auto"
        >
            {{ dashboardStore.fetchErrorMessage }}
        </BaseMessage>

        <div class="flex flex-col gap-10 w-full mx-auto flex-1 min-w-0 pb-4">
            <!-- ================================ -->
            <!-- KPI -->
            <!-- ================================ -->
            <OptimDashboardKpiSection
                :loading="dashboardStore.isFetchingDashboard"
                :title-total="t('totalSection')"
                :title-mean="t('meanSection')"
                :title-other="t('otherSection')"
                :total-kpis="getTotalKpis"
                :mean-kpis="getMeanKpis"
                :other-kpis="getOtherKpis"
                :kpi-htva-caption="t('kpiHtva')"
            />

            <!-- ================================ -->
            <!-- TABLEAU -->
            <!-- ================================ -->
            <OptimDashboardAnalysesTable
                v-if="getSynthesis"
                :rows="getTableRows"
                :loading="dashboardStore.isFetchingDashboard"
                :title="t('detailsSection')"
                :empty-message="t('emptyTable')"
                :col-segment="t('colSegment')"
                :col-idsource="t('colIdsource')"
                :col-address="t('colAddress')"
                :col-economy="t('colEconomy')"
                :filter-option-c5="t('filterC5')"
                :filter-option-c4="t('filterC4')"
                :filter-option-c2="t('filterC2')"
            />
        </div>

        <OptimDashboardExportDialog
            v-model:visible="isExportDialogVisible"
            :header="t('modalTitle')"
            :message="t('modalMessage')"
            :confirm-label="t('modalConfirm')"
            :cancel-label="t('modalCancel')"
            :choice-c5="t('modalChoice1')"
            :choice-c4="t('modalChoice2')"
            :choice-c2="t('modalChoice3')"
            :default-segments="defaultExportSegments"
            @confirm="onExportConfirmed"
        />
    </div>
</template>
